import * as React from 'react';
import styled from 'styled-components';

import { Button } from '../../../components/ui/Button';
import { Modal } from '../../../components/ui/Modal';
import { getReportCopy } from '../lib/reportCopy';
import type { MarketingReportTimeRange } from '../types';

const SummaryList = styled.dl`
  display: grid;
  grid-template-columns: minmax(7rem, auto) 1fr;
  gap: var(--space-sm) var(--space-md);
  margin: 0;

  dt {
    color: var(--text-secondary);
    font-weight: 600;
  }

  dd {
    color: var(--text-primary);
    margin: 0;
  }
`;

const Section = styled.section`
  margin-top: var(--space-lg);

  h3 {
    color: var(--text-primary);
    font-size: 1rem;
    margin: 0 0 var(--space-sm);
  }

  ul {
    margin: 0;
    padding-left: var(--space-xl);
  }
`;

const WarningSection = styled(Section)<{ $hasWarnings: boolean }>`
  border: 1px solid
    ${(props) => (props.$hasWarnings ? 'var(--warning-200)' : 'var(--border)')};
  border-radius: var(--radius-md);
  background: ${(props) =>
    props.$hasWarnings ? 'var(--warning-50)' : 'var(--surface-strong)'};
  padding: var(--space-md);
  color: ${(props) =>
    props.$hasWarnings ? 'var(--warning-700)' : 'var(--text-secondary)'};
`;

export interface ReportPreflightDialogProps {
  isOpen: boolean;
  timeRange: MarketingReportTimeRange;
  locale: string;
  templateId?: string | null;
  sections?: readonly string[];
  warnings?: readonly string[];
  isGenerating: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

function formatDate(value: string, locale: string): string {
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(
    parsed,
  );
}

export const ReportPreflightDialog: React.FC<ReportPreflightDialogProps> = ({
  isOpen,
  timeRange,
  locale,
  templateId,
  sections,
  warnings = [],
  isGenerating,
  onClose,
  onConfirm,
}) => {
  const copy = React.useMemo(() => getReportCopy(locale), [locale]);
  const includedSections = sections ?? copy.preflight.defaultSections;
  const period =
    timeRange.type === 'CUSTOM' && timeRange.start && timeRange.end
      ? copy.preflight.customPeriod
          .replace('{{start}}', formatDate(timeRange.start, locale))
          .replace('{{end}}', formatDate(timeRange.end, locale))
      : copy.preflight.lastFullMonth;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={copy.preflight.title}
      description={copy.preflight.description}
      closeOnOverlayClick={!isGenerating}
      closeOnEscape={!isGenerating}
      showCloseButton={!isGenerating}
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isGenerating}
          >
            {copy.button.cancel}
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={onConfirm}
            loading={isGenerating}
            disabled={isGenerating}
          >
            {copy.button.confirm}
          </Button>
        </>
      }
    >
      <div aria-busy={isGenerating}>
        <SummaryList>
          <dt>{copy.preflight.period}</dt>
          <dd>{period}</dd>
          <dt>{copy.preflight.template}</dt>
          <dd>{templateId || copy.preflight.defaultTemplate}</dd>
          <dt>{copy.preflight.language}</dt>
          <dd>{copy.preflight.spanish}</dd>
        </SummaryList>

        <Section aria-labelledby="report-preflight-sections">
          <h3 id="report-preflight-sections">{copy.preflight.sections}</h3>
          <ul>
            {includedSections.map((section) => (
              <li key={section}>{section}</li>
            ))}
          </ul>
        </Section>

        <WarningSection
          $hasWarnings={warnings.length > 0}
          aria-labelledby="report-preflight-warnings"
        >
          <h3 id="report-preflight-warnings">{copy.preflight.warnings}</h3>
          {warnings.length > 0 ? (
            <ul>
              {warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          ) : (
            <p>{copy.preflight.noWarnings}</p>
          )}
        </WarningSection>
      </div>
    </Modal>
  );
};
